/**
 * Country Select Component
 * Handles country selection with search functionality and currency integration
 */

import React, { useState, useMemo, useCallback } from 'react';
import { Label } from '../ui/label';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Popover, PopoverContent, PopoverTrigger } from '../ui/popover';
import { Command, CommandEmpty, CommandGroup, CommandInput, CommandItem, CommandList } from '../ui/command';
import { Check, ChevronsUpDown, AlertCircle, Globe } from 'lucide-react';
import { cn } from '../../lib/utils';
import { COUNTRIES, getCountryByCode, searchCountries, type CountryOption } from '../../utils/countries';

interface CountrySelectComponentProps {
  value?: string;
  onCountryChange: (countryCode: string, currencyCode: string) => void;
  disabled?: boolean;
  error?: string;
}

export const CountrySelectComponent: React.FC<CountrySelectComponentProps> = ({
  value,
  onCountryChange,
  disabled = false,
  error,
}) => {
  const [open, setOpen] = useState(false);
  const [searchQuery, setSearchQuery] = useState('');

  // Get the selected country details
  const selectedCountry = useMemo(() => {
    return value ? getCountryByCode(value) : null;
  }, [value]);

  // Filter countries based on search query
  const filteredCountries = useMemo(() => {
    if (!searchQuery.trim()) {
      return COUNTRIES;
    }
    return searchCountries(searchQuery);
  }, [searchQuery]);

  const handleCountrySelect = useCallback(
    (country: CountryOption) => {
      onCountryChange(country.code, country.currency);
      setOpen(false);
      setSearchQuery('');
    },
    [onCountryChange]
  );

  const handleOpenChange = useCallback((newOpen: boolean) => {
    setOpen(newOpen);
    if (!newOpen) {
      setSearchQuery('');
    }
  }, []);

  return (
    <div className="space-y-2">
      <Label>Country</Label>
      <p className="text-sm text-muted-foreground">
        Select your country to set currency and regional settings
      </p>

      <Popover open={open} onOpenChange={handleOpenChange}>
        <PopoverTrigger asChild>
          <Button
            variant="outline"
            role="combobox"
            aria-expanded={open}
            className={cn(
              "w-full justify-between",
              !selectedCountry && "text-muted-foreground",
              error && "border-red-300"
            )}
            disabled={disabled}
          >
            <div className="flex items-center space-x-2">
              {selectedCountry ? (
                <>
                  <Globe className="h-4 w-4" />
                  <span>{selectedCountry.name}</span>
                  <span className="text-sm text-muted-foreground">
                    ({selectedCountry.currency})
                  </span>
                </>
              ) : (
                <>
                  <Globe className="h-4 w-4" />
                  <span>Select country...</span>
                </>
              )}
            </div>
            <ChevronsUpDown className="ml-2 h-4 w-4 shrink-0 opacity-50" />
          </Button>
        </PopoverTrigger>
        
        <PopoverContent className="w-full p-0" align="start">
          <Command>
            <CommandInput
              placeholder="Search countries..."
              value={searchQuery}
              onValueChange={setSearchQuery}
            />
            <CommandList>
              <CommandEmpty>No countries found.</CommandEmpty>
              <CommandGroup>
                {filteredCountries.map((country) => (
                  <CommandItem
                    key={country.code}
                    value={`${country.name} ${country.code} ${country.currency}`}
                    onSelect={() => handleCountrySelect(country)}
                    className="flex items-center justify-between"
                  >
                    <div className="flex items-center space-x-2">
                      <Globe className="h-4 w-4" />
                      <span>{country.name}</span>
                    </div>
                    <div className="flex items-center space-x-2">
                      <span className="text-sm text-muted-foreground">
                        {country.currency}
                      </span>
                      {selectedCountry?.code === country.code && (
                        <Check className="h-4 w-4" />
                      )}
                    </div>
                  </CommandItem>
                ))}
              </CommandGroup>
            </CommandList>
          </Command>
        </PopoverContent>
      </Popover>

      {/* Selected Country Details */}
      {selectedCountry && (
        <div className="mt-3 p-3 bg-muted/50 rounded-lg">
          <div className="flex items-center justify-between">
            <div className="flex items-center space-x-3">
              <Globe className="h-6 w-6 text-muted-foreground" />
              <div>
                <p className="text-sm font-medium">{selectedCountry.name}</p>
                <p className="text-xs text-muted-foreground">
                  Code: {selectedCountry.code}
                </p>
              </div>
            </div>
            <div className="text-right">
              <p className="text-sm font-medium">
                {selectedCountry.currency}
              </p>
              <p className="text-xs text-muted-foreground">
                {selectedCountry.currencySymbol}
              </p>
            </div>
          </div>
        </div>
      )}

      {/* Error Display */}
      {error && (
        <div className="flex items-center space-x-2 text-sm text-red-600">
          <AlertCircle className="h-4 w-4" />
          <span>{error}</span>
        </div>
      )}

      {/* Helper Text */}
      <p className="text-xs text-muted-foreground">
        Your country selection will determine the default currency format and regional settings for your workspace.
      </p>
    </div>
  );
};

export default CountrySelectComponent;
